import { SupportedCountryCode } from '../../utils/currency'
import Environment from '../../utils/Environment'

export type ExpiredPaywallProductCode = 'monthly_10' | '0d_annual_70' | '0d_annual_36' | '0d_annual_35'

export type ExpiredPaywallPrice = {
  code: string
  price: number
  daysFree: number
  sheetLabel: string
  productCode: ExpiredPaywallProductCode
  title: string | JSX.Element
  subtitle: string
  pricingText: string | JSX.Element
  perMonth: string
  annualPrice: string
}

export type ExpiredCheckoutScreenProps = {
  initialOption: ExpiredPaywallProductCode
  otherOption: ExpiredPaywallProductCode
  isStudent: boolean
}

export const getExpiredPaywallProducts = (): Record<
  ExpiredPaywallProductCode,
  Record<SupportedCountryCode, ExpiredPaywallPrice>
> => {
  // Helper to extract currency symbol and format perMonth
  const formatPerMonth = (annual: string): string => {
    // Extract currency symbol and numeric value
    const match = annual.match(/([^\d.,]+)?([\d.,]+)/)
    if (!match) return ''
    const symbol = match[1] || ''
    const value = parseFloat(match[2].replace(',', '.'))
    if (isNaN(value)) return ''
    const perMonth = (value / 12).toFixed(2)
    // For euro, use comma as decimal separator
    if (symbol.includes('€')) {
      return `${perMonth.replace('.', ',')} €`
    }
    return `${symbol}${perMonth}`
  }
  // Helper to format annual price for monthly plans
  const formatAnnualFromPerMonth = (perMonth: string): string => {
    const match = perMonth.match(/([^\d.,]+)?([\d.,]+)/)
    if (!match) return ''
    const symbol = match[1] || ''
    const value = parseFloat(match[2].replace(',', '.'))
    if (isNaN(value)) return ''
    const annual = (value * 12).toFixed(2)
    if (symbol.includes('€')) {
      return `${annual.replace('.', ',')} €`
    }
    return `${symbol}${annual}`
  }
  return {
    monthly_10: {
      US: {
        code: Environment.isProduction ? 'price_1RjKRkH7jib4x1GyIAetsVzI' : 'price_1Rih5tH7jib4x1GyHC414izz',
        price: 999,
        daysFree: 0,
        sheetLabel: 'Monthly Plan',
        productCode: 'monthly_10',
        title: 'Monthly', // not used
        subtitle: 'Billed monthly', //not used
        pricingText: '$9.99/mo', //not used
        perMonth: '$9.99',
        annualPrice: formatAnnualFromPerMonth('$9.99'),
      },
      GB: {
        code: Environment.isProduction ? 'price_1RjKRlH7jib4x1GyV564d7Oq' : 'price_1Rih5tH7jib4x1GyargnZbNJ',
        price: 899,
        daysFree: 0,
        sheetLabel: 'Monthly Plan',
        productCode: 'monthly_10',
        title: 'Monthly',
        subtitle: 'Billed monthly',
        pricingText: '£8.99/mo',
        perMonth: '£8.99',
        annualPrice: formatAnnualFromPerMonth('£8.99'),
      },
      CA: {
        code: Environment.isProduction ? 'price_1RjKRkH7jib4x1GynjRwDh4l' : 'price_1Rih5tH7jib4x1GyTZn5n8PS',
        price: 1299,
        daysFree: 0,
        sheetLabel: 'Monthly Plan',
        productCode: 'monthly_10',
        title: 'Monthly',
        subtitle: 'Billed monthly',
        pricingText: 'CA$12.99/mo',
        perMonth: 'CA$12.99',
        annualPrice: formatAnnualFromPerMonth('CA$12.99'),
      },
      AU: {
        code: Environment.isProduction ? 'price_1RjKRlH7jib4x1GyDWXjXiRI' : 'price_1Rih5uH7jib4x1GyfPpRdkEy',
        price: 1499,
        daysFree: 0,
        sheetLabel: 'Monthly Plan',
        productCode: 'monthly_10',
        title: 'Monthly',
        subtitle: 'Billed monthly',
        pricingText: 'AU$14.99/mo',
        perMonth: 'AU$14.99',
        annualPrice: formatAnnualFromPerMonth('AU$14.99'),
      },
      DE: {
        code: Environment.isProduction ? 'price_1RjKRkH7jib4x1GyqaOUlSQo' : 'price_1Rih5tH7jib4x1GyVU10tsBG',
        price: 999,
        daysFree: 0,
        sheetLabel: 'Monthly Plan',
        productCode: 'monthly_10',
        title: 'Monthly',
        subtitle: 'Billed monthly',
        pricingText: '9,99 €/mo',
        perMonth: '9,99 €',
        annualPrice: formatAnnualFromPerMonth('9,99 €'),
      },
    },
    '0d_annual_70': {
      US: {
        code: Environment.isProduction ? 'price_1RjKWNH7jib4x1GyjYc1sTiN' : 'price_1RjKUmH7jib4x1Gyh50C4lby',
        price: 6999,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_70',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: '$69.99/yr',
        perMonth: formatPerMonth('$69.99'),
        annualPrice: '$69.99',
      },
      GB: {
        code: Environment.isProduction ? 'price_1RjKWOH7jib4x1Gy5ZaYqdPy' : 'price_1RjKUnH7jib4x1GycXPwqQ1g',
        price: 5999,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_70',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: '£59.99/yr',
        perMonth: formatPerMonth('£59.99'),
        annualPrice: '£59.99',
      },
      CA: {
        code: Environment.isProduction ? 'price_1RjKWOH7jib4x1GyaAouEdRB' : 'price_1RjKUnH7jib4x1GyEjJhQkLx',
        price: 9999,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_70',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: 'CA$99.99/yr',
        perMonth: formatPerMonth('CA$99.99'),
        annualPrice: 'CA$99.99',
      },
      AU: {
        code: Environment.isProduction ? 'price_1RjKWOH7jib4x1GyblGtA0i0' : 'price_1RjKUnH7jib4x1GyMwGP7EqG',
        price: 11999,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_70',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: 'AU$119.99/yr',
        perMonth: formatPerMonth('AU$119.99'),
        annualPrice: 'AU$119.99',
      },
      DE: {
        code: Environment.isProduction ? 'price_1RjKWOH7jib4x1Gy272R0t6d' : 'price_1RjKUnH7jib4x1GypK7pVRW7',
        price: 6999,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_70',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: '69,99 €/yr',
        perMonth: formatPerMonth('69,99 €'),
        annualPrice: '69,99 €',
      },
    },
    '0d_annual_36': {
      US: {
        code: Environment.isProduction ? 'price_1RjKYMH7jib4x1Gy4jWQW8En' : 'price_1RjKXQH7jib4x1GyCaNghwRg',
        price: 3599,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_36',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: '$35.99/yr',
        perMonth: formatPerMonth('$35.99'),
        annualPrice: '$35.99',
      },
      GB: {
        code: Environment.isProduction ? 'price_1RjKYNH7jib4x1GyV7DdcvkP' : 'price_1RjKXRH7jib4x1GyV7dqFbiR',
        price: 2999,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_36',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: '£29.99/yr',
        perMonth: formatPerMonth('£29.99'),
        annualPrice: '£29.99',
      },
      CA: {
        code: Environment.isProduction ? 'price_1RjKYNH7jib4x1Gys9FraeZA' : 'price_1RjKXRH7jib4x1GyB0cH3ggQ',
        price: 4799,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_36',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: 'CA$47.99/yr',
        perMonth: formatPerMonth('CA$47.99'),
        annualPrice: 'CA$47.99',
      },
      AU: {
        code: Environment.isProduction ? 'price_1RjKYNH7jib4x1GyzlXZhVvg' : 'price_1RjKXRH7jib4x1GyBv3tDSEi',
        price: 5099,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_36',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: 'AU$50.99/yr',
        perMonth: formatPerMonth('AU$50.99'),
        annualPrice: 'AU$50.99',
      },
      DE: {
        code: Environment.isProduction ? 'price_1RjKYMH7jib4x1GyTePn2q5D' : 'price_1RjKXRH7jib4x1GyB2p678bV',
        price: 3499,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_36',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: '34,99 €/yr',
        perMonth: formatPerMonth('34,99 €'),
        annualPrice: '34,99 €',
      },
    },
    '0d_annual_35': {
      US: {
        code: Environment.isProduction ? 'price_1QG193H7jib4x1GyniiXrCeR' : 'price_1QG0d5H7jib4x1GyClQ9zGZE',
        price: 3499,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_35',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: '$34.99/yr',
        perMonth: formatPerMonth('$34.99'),
        annualPrice: '$34.99',
      },
      GB: {
        code: Environment.isProduction ? 'price_1QG195H7jib4x1GycBd6Dnaz' : 'price_1QG0d8H7jib4x1Gytolx08Hm',
        price: 2999,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_35',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: '£29.99/yr',
        perMonth: formatPerMonth('£29.99'),
        annualPrice: '£29.99',
      },
      CA: {
        code: Environment.isProduction ? 'price_1QG194H7jib4x1GySMYh7G7y' : 'price_1QG0d6H7jib4x1Gy49QTScHo',
        price: 4999,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_35',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: 'CA$49.99/yr',
        perMonth: formatPerMonth('CA$49.99'),
        annualPrice: 'CA$49.99',
      },
      AU: {
        code: Environment.isProduction ? 'price_1QG194H7jib4x1GysIHVxhOO' : 'price_1QG0d7H7jib4x1GyJ06KG7Bb',
        price: 5999,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_35',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: 'AU$59.99/yr',
        perMonth: formatPerMonth('AU$59.99'),
        annualPrice: 'AU$59.99',
      },
      DE: {
        code: Environment.isProduction ? 'price_1QG193H7jib4x1GybesNw09P' : 'price_1QG0d6H7jib4x1GyYj94lxez',
        price: 3499,
        daysFree: 0,
        sheetLabel: 'Annual Plan',
        productCode: '0d_annual_35',
        title: 'Annual',
        subtitle: 'Billed yearly',
        pricingText: '34,99 €/yr',
        perMonth: formatPerMonth('34,99 €'),
        annualPrice: '34,99 €',
      },
    },
  }
}

export const getExpiredPaywallProductConfigs = (): Record<
  ExpiredPaywallProductCode,
  Record<SupportedCountryCode, ExpiredPaywallPrice>
> => {
  return getExpiredPaywallProducts()
}
